import React from "react";
import { FileText, Upload, ScanSearch, Sparkles, Package } from "lucide-react";
import { useApp } from "@/context/AppContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const ILLUSTRATION =
  "https://static.prod-images.emergentagent.com/jobs/52065e48-0c31-438c-b50d-d2dfb044889e/images/5aff7375f0f602d905ef6d679ed93c1b412832aa77f9276f1ac6720ed6f1d8fe.jpeg";

export default function SpecsAnalysisView() {
  const { t } = useApp();

  const steps = [
    { icon: Upload, title: t("specsStep1"), desc: t("specsStep1d") },
    { icon: ScanSearch, title: t("specsStep2"), desc: t("specsStep2d") },
    { icon: Package, title: t("specsStep3"), desc: t("specsStep3d") },
  ];

  return (
    <div data-testid="specs-analysis-view" className="mx-auto max-w-5xl">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
        <div>
          <span
            data-testid="specs-coming-soon-badge"
            className="inline-flex items-center gap-1.5 border border-primary bg-primary/10 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-primary"
          >
            <Sparkles className="h-3.5 w-3.5" /> {t("specsComingSoon")}
          </span>

          <h1 className="mt-5 font-display text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl">
            {t("specsTitle")}
          </h1>

          <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">
            {t("specsDesc")}
          </p>

          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="mt-8 inline-block">
                  <button
                    data-testid="specs-upload-disabled-btn"
                    disabled
                    aria-disabled="true"
                    className="pointer-events-none flex cursor-not-allowed items-center gap-2 border border-border bg-muted px-6 py-3.5 text-sm font-bold uppercase tracking-wide text-muted-foreground opacity-50"
                  >
                    <Upload className="h-4 w-4" /> {t("specsUpload")}
                  </button>
                </span>
              </TooltipTrigger>
              <TooltipContent
                data-testid="specs-upload-tooltip"
                side="bottom"
                className="max-w-xs rounded-none bg-foreground text-background"
              >
                {t("specsTooltip")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className="relative">
          <div className="absolute -inset-3 border border-dashed border-border" aria-hidden />
          <img
            data-testid="specs-illustration"
            src={ILLUSTRATION}
            alt={t("specsTitle")}
            className="relative w-full border border-border object-cover shadow-lg"
          />
        </div>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-3">
        {steps.map((s, i) => (
          <div key={i} className="flex flex-col gap-3 bg-card p-6">
            <div className="flex h-11 w-11 items-center justify-center border border-border bg-background text-primary">
              <s.icon className="h-5 w-5" />
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">0{i + 1}</span>
              <h3 className="font-display text-lg font-bold tracking-tight">{s.title}</h3>
            </div>
            <p className="text-sm text-muted-foreground">{s.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 flex items-center justify-center gap-2 text-muted-foreground/60">
        <FileText className="h-4 w-4" />
        <span className="text-xs font-mono uppercase tracking-widest">METEROX · Beta</span>
      </div>
    </div>
  );
}
